import AudienceMergeMode from '@/Enums/AudienceTemplates/MergeMode.enum';
import AudienceTemplateModalType from '@/Enums/AudienceTemplates/ModalType.enum';
import AudienceTemplateModalVariant from '@/Enums/AudienceTemplates/ModalVariant.enum';
import type { IForecasterAudienceBlock } from '@/Interfaces/AudienceTemplates/AudienceBlock.interface';
import type { IAudienceTemplate } from '@/Interfaces/AudienceTemplates/AudienceTemplate.interface';
import type { IBaseAudienceTargeting } from '@/Interfaces/Audiences/Audience.interface';
import type { IAudienceControlOption } from '@/Interfaces/ControlOption.interface';
import AudienceTargetingService from '@/Services/AudienceTargeting.service';
import AudienceListContainer from '@/components/AudienceList';
import { applyTemplateConfig } from '@/helpers/audienceTemplates/applyTemplate';
import { mergeTemplateTargeting } from '@/helpers/audienceTemplates/mergeTemplate';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectAudienceTemplates,
  selectAudienceTemplatesLoading,
  selectAudienceTemplatesSaving,
} from '@/store/selectors/audienceTemplates.selectors';
import { selectIsAdmin } from '@/store/selectors/me.selectors';
import {
  createAudienceTemplate,
  deleteAudienceTemplate,
  getAudienceTemplates,
  updateAudienceTemplate,
} from '@/store/slices/audienceTemplates.thunks';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import TemplatesList from './TemplatesList';

const modeButtonSx = { borderRadius: '32px', minWidth: 56 } as const;

interface IProps {
  open: boolean;
  initialType: AudienceTemplateModalType;
  options: IAudienceControlOption<number>[];
  currentValue: IBaseAudienceTargeting<IAudienceControlOption<number>>;
  allowExtendedGrouping: boolean;
  onClose: () => void;
  onApply: (value: IBaseAudienceTargeting<IAudienceControlOption<number>>) => void;
  variant?: AudienceTemplateModalVariant;
  onApplyBlock?: (block: IForecasterAudienceBlock) => void;
  onAddAndRow?: () => void;
  canAddAndRow?: boolean;
}

const AudienceTemplatesModal = ({
  open,
  initialType,
  options,
  currentValue,
  allowExtendedGrouping,
  onClose,
  onApply,
  variant = AudienceTemplateModalVariant.Cnf,
  onApplyBlock,
  onAddAndRow,
  canAddAndRow,
}: IProps) => {
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(selectIsAdmin);
  const templates = useAppSelector(selectAudienceTemplates);
  const loading = useAppSelector(selectAudienceTemplatesLoading);
  const saving = useAppSelector(selectAudienceTemplatesSaving);

  const [type, setType] = useState(initialType);
  // Track whether the form was reached from the templates list, so Cancel knows
  // whether to return to the list or close the modal outright.
  const [cameFromList, setCameFromList] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [builderValue, setBuilderValue] = useState<IBaseAudienceTargeting<IAudienceControlOption<number>>>(
    AudienceTargetingService.defaultAudienceTargeting,
  );
  const [mode, setMode] = useState<AudienceMergeMode>(AudienceMergeMode.Or);
  const [appliedSummary, setAppliedSummary] = useState<{ name: string; mode: AudienceMergeMode }[]>([]);

  useEffect(() => {
    if (open) {
      setType(initialType);
      setCameFromList(false);
      setMode(AudienceMergeMode.Or);
      setAppliedSummary([]);
      setEditingId(null);
      setName('');
      setDescription('');
      setBuilderValue(AudienceTargetingService.defaultAudienceTargeting);
      dispatch(getAudienceTemplates());
    }
  }, [open, initialType, dispatch]);

  const isForm = type === AudienceTemplateModalType.Create || type === AudienceTemplateModalType.Edit;

  const canSave = useMemo(
    () => name.trim().length > 0 && AudienceTargetingService.checkAudiencesInTargeting(builderValue),
    [name, builderValue],
  );

  const resetForm = () => {
    setEditingId(null);
    setName('');
    setDescription('');
    setBuilderValue(AudienceTargetingService.defaultAudienceTargeting);
  };

  const startCreate = () => {
    resetForm();
    setCameFromList(true);
    setType(AudienceTemplateModalType.Create);
  };

  const startEdit = (template: IAudienceTemplate) => {
    const { value, missingCount } = applyTemplateConfig(template.config, options);
    if (missingCount > 0) {
      toast.warn(`${missingCount} audience(s) in this template aren't available in this channel.`);
    }
    setEditingId(template.id);
    setName(template.name);
    setDescription(template.description ?? '');
    setBuilderValue(value);
    setCameFromList(true);
    setType(AudienceTemplateModalType.Edit);
  };

  const handleApply = (template: IAudienceTemplate) => {
    const { value, missingCount } = applyTemplateConfig(template.config, options);
    const hasAudiences = !!value.include?.length || !!value.exclude?.length;
    if (!hasAudiences) {
      toast.error('None of this template’s audiences are available in this channel.');
      return;
    }
    if (missingCount > 0) {
      toast.warn(`${missingCount} audience(s) weren’t available in this channel and were skipped.`);
    }
    const base = currentValue ?? AudienceTargetingService.defaultAudienceTargeting;
    onApply(mergeTemplateTargeting(base, value, mode));
    setAppliedSummary(prev => [...prev, { name: template.name, mode }]);
    toast.success(`Applied “${template.name}” (${mode === AudienceMergeMode.Or ? 'OR' : 'AND'}).`);
  };

  const handleApplyBlock = (template: IAudienceTemplate) => {
    const { value, missingCount } = applyTemplateConfig(template.config, options);
    const hasAudiences = !!value.include?.length || !!value.exclude?.length;
    if (!hasAudiences) {
      toast.error('None of this template’s audiences are available in this channel.');
      return;
    }
    if (missingCount > 0) {
      toast.warn(`${missingCount} audience(s) weren’t available in this channel and were skipped.`);
    }
    onApplyBlock?.({ id: `${template.id}-${Date.now()}`, label: template.name, targeting: value });
    toast.success(`Added “${template.name}”.`);
  };

  const handleDelete = async (template: IAudienceTemplate) => {
    if (!window.confirm(`Delete template “${template.name}”?`)) {
      return;
    }
    try {
      await dispatch(deleteAudienceTemplate(template.id)).unwrap();
      toast.success('Template deleted.');
    } catch {
      toast.error('Could not delete the template. Please try again.');
    }
  };

  const handleSave = async () => {
    const config = AudienceTargetingService.audienceControlOptionsToTargeting(builderValue);
    const payload = { name: name.trim(), description: description.trim() || undefined, config };
    try {
      if (type === AudienceTemplateModalType.Edit && editingId != null) {
        await dispatch(updateAudienceTemplate({ id: editingId, payload })).unwrap();
        toast.success('Template updated.');
      } else {
        await dispatch(createAudienceTemplate(payload)).unwrap();
        toast.success('Template created.');
      }
      resetForm();
      setType(AudienceTemplateModalType.View);
    } catch {
      toast.error('Could not save the template. Please try again.');
    }
  };

  const title =
    type === AudienceTemplateModalType.View
      ? 'Audience Templates'
      : type === AudienceTemplateModalType.Edit
        ? 'Edit Template'
        : 'Create Template';

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {isForm ? (
          <div className='flex flex-col gap-4 pt-2'>
            <TextField
              label='Name'
              value={name}
              onChange={event => setName(event.target.value)}
              required
              fullWidth
              slotProps={{ htmlInput: { maxLength: 255 } }}
            />
            <TextField
              label='Description'
              value={description}
              onChange={event => setDescription(event.target.value)}
              fullWidth
              slotProps={{ htmlInput: { maxLength: 1000 } }}
            />
            <AudienceListContainer
              options={options}
              value={builderValue}
              onChange={setBuilderValue}
              allowExtendedGrouping={allowExtendedGrouping}
            />
          </div>
        ) : (
          <div className='flex flex-col gap-3 pt-1'>
            {variant === AudienceTemplateModalVariant.Cnf ? (
              <div className='flex items-center gap-2' role='group' aria-label='Combine mode'>
                <span className='text-sm text-gray-600'>Combine with current audience:</span>
                <Button
                  size='small'
                  variant={mode === AudienceMergeMode.Or ? 'contained' : 'outlined'}
                  sx={modeButtonSx}
                  onClick={() => setMode(AudienceMergeMode.Or)}
                  aria-pressed={mode === AudienceMergeMode.Or}
                >
                  OR
                </Button>
                <Button
                  size='small'
                  variant={mode === AudienceMergeMode.And ? 'contained' : 'outlined'}
                  sx={modeButtonSx}
                  onClick={() => setMode(AudienceMergeMode.And)}
                  aria-pressed={mode === AudienceMergeMode.And}
                >
                  AND
                </Button>
              </div>
            ) : (
              <div className='flex items-center gap-2'>
                <span className='text-sm text-gray-600'>Add templates (OR by default):</span>
                <Button
                  size='small'
                  variant='outlined'
                  sx={{ borderRadius: '32px' }}
                  disabled={!canAddAndRow}
                  onClick={() => onAddAndRow?.()}
                >
                  AND
                </Button>
              </div>
            )}
            <TemplatesList
              templates={templates}
              loading={loading}
              isAdmin={isAdmin}
              onApply={variant === AudienceTemplateModalVariant.Blocks ? handleApplyBlock : handleApply}
              onEdit={startEdit}
              onDelete={handleDelete}
            />
            {appliedSummary.length > 0 && (
              <p className='text-sm text-gray-600'>
                Applied this session:{' '}
                {appliedSummary.map((entry, index) => (
                  <span key={`${entry.name}-${index}`}>
                    {index > 0 && <strong>{` ${entry.mode === AudienceMergeMode.Or ? 'OR' : 'AND'} `}</strong>}
                    {entry.name}
                  </span>
                ))}
              </p>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {isForm ? (
          <>
            <Button onClick={() => (cameFromList ? setType(AudienceTemplateModalType.View) : onClose())}>Cancel</Button>
            <Button variant='contained' disabled={!canSave || saving} onClick={handleSave}>
              Save
            </Button>
          </>
        ) : (
          <>
            {isAdmin && <Button onClick={startCreate}>Create New</Button>}
            <Button onClick={onClose}>Close</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AudienceTemplatesModal;
